//@flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import { Input } from 'reactstrap';
import capitalize from 'lodash/capitalize';
import { connect } from 'react-redux';

import { Counter, Toggler } from '../../../../components';
import {
  selectGuideType,
  updateNumberOfPorter,
} from '../../../../redux/actions/trekPaymentReviewActions';
import CollapseContainer from './CollapseContainer';

type Money = {
  amount: number,
  currency: string,
};
type Props = {
  noOfPorter: number,
  selectedGuideType: string,
  onChangeGuideType: (guideType: string) => void,
  onChangeNumberOfPorter: (noOfPorter: number) => void,
  leaderPrice: Money,
  guidePrice: Money,
  porters: Money,
  totalGuidePrice: Money,
  totalLeaderPrice: Money,
  totalAdditionalPrice: number,
  numberOfDays: number,
  whenPorterSelected: boolean,
  isOneWayFlight: boolean,
};

const mapStateToProps = ({
  trekPayment: { noOfPorter, selectedGuideType, guides, porters, flights },
  selectedTrek: {
    trek: { number_of_days },
  },
}) => ({
  noOfPorter,
  selectedGuideType,
  leaderPrice: guides && guides.leader,
  guidePrice: guides && guides.guide,
  whenPorterSelected: noOfPorter > 0,
  numberOfDays: number_of_days,
  porters,
  isOneWayFlight: flights.length === 1,
});

const mapDispatchToProps = dispatch => ({
  onChangeGuideType: guideType => dispatch(selectGuideType(guideType)),
  onChangeNumberOfPorter: noOfPorter => dispatch(updateNumberOfPorter(noOfPorter)),
});

const AdditionalServices = ({
  noOfPorter,
  selectedGuideType,
  onChangeGuideType,
  onChangeNumberOfPorter,
  leaderPrice,
  guidePrice,
  totalAdditionalPrice,
  numberOfDays,
  porters,
  whenPorterSelected,
  isOneWayFlight,
}: Props) => {
  const renderGuideRadio = type => (
    <input
      type="radio"
      name={type}
      checked={selectedGuideType === type}
      onClick={e => onChangeGuideType(e.target.name)}
    />
  );

  const EXTRA_FLIGHT_PRICE = isOneWayFlight ? 80 : 160;
  const FLIGHT_TEXT = isOneWayFlight ? 'One Way Flight' : 'Two Way Flight';

  return (
    <div className="trekreview">
      {/* <h2 className="trekreview-title">ADD GUIDES AND PORTERS</h2> */}
      <CollapseContainer cost={totalAdditionalPrice} title="ADD GUIDES AND PORTERS">
        <ul className="trekreview-steps">
          {leaderPrice && (
            <AdditionalServiceType
              type="leader"
              price={leaderPrice}
              days={numberOfDays}
              input={renderGuideRadio('leader')}
              extra={EXTRA_FLIGHT_PRICE}
              message={
                <li>
                  [Certified, Highly Experienced, Great communication skills and Expertise
                  in leading big groups]
                  <br />
                  {generatePriceSummaryMessage(
                    'Leader',
                    leaderPrice,
                    numberOfDays,
                    EXTRA_FLIGHT_PRICE,
                    FLIGHT_TEXT
                  )}
                </li>
              }
            />
          )}
          {guidePrice && (
            <AdditionalServiceType
              type="guide"
              price={guidePrice}
              days={numberOfDays}
              input={renderGuideRadio('guide')}
              extra={EXTRA_FLIGHT_PRICE}
              message={
                <li>
                  [Certified Trekking Guide with Capacity to lead treks.]
                  <br />
                  {generatePriceSummaryMessage(
                    'Guide',
                    guidePrice,
                    numberOfDays,
                    EXTRA_FLIGHT_PRICE,
                    FLIGHT_TEXT
                  )}
                </li>
              }
            />
          )}
          <AdditionalServiceType type="none" input={renderGuideRadio('none')} /> <hr />
          {porters && (
            <Toggler>
              {({ on, toggle }) => (
                <Fragment>
                  <AdditionalServiceType
                    type="porters"
                    price={porters}
                    days={numberOfDays - 1}
                    extra={0}
                    input={
                      <Input
                        type="checkbox"
                        checked={on}
                        onClick={() => {
                          // resuming the porter count as 1 when porter
                          // checkbox was previously not clicked
                          if (!whenPorterSelected) onChangeNumberOfPorter(1);

                          // when the user unchecks the porter
                          // the number of porter should be zero
                          if (on) onChangeNumberOfPorter(0);

                          toggle();
                        }}
                      />
                    }
                    message={
                      <li>
                        {generatePriceSummaryMessage('Porter', porters, numberOfDays - 1)}
                        [Porters will only join from the trek start point (eg.from Lukla in
                        EBC trek) so no additional cost (flights/ land transportation) will
                        be needed. If you have specific requirement, please contact us at{' '}
                        <a href="mailto:honeyhunter@honeyguideapps.com">
                          honeyhunter@honeyguideapps.com
                        </a>
                        ]
                        <br /> The maximum weight carried by a porter is 30kg. Generally, 1
                        porter is required for 2 trekkers.
                        {on && (
                          <Counter
                            value={noOfPorter}
                            onChange={onChangeNumberOfPorter}
                            title="Select number of porters you want."
                          />
                        )}
                      </li>
                    }
                  />
                </Fragment>
              )}
            </Toggler>
          )}
        </ul>
      </CollapseContainer>
    </div>
  );
};

type RadioProps = {
  type: null | string,
  input: Node,
  extra?: number,
  days?: number,
  price?: Money,
  message?: Node,
};

const AdditionalServiceType = ({ price, type, message, days, input, extra }: RadioProps) =>
  type ? (
    <li className="mb-3">
      <label className="mb-0">
        {input}
        <span />
        <strong>{capitalize(type)}</strong>
      </label>
      {price && (
        <span
          className="price float-right"
          style={{ color: '#e91e63', textAlign: 'right' }}
        >
          {price.currency} {Number(price.amount * days + extra).toLocaleString()} <br />
        </span>
      )}
      <ol className="dot-list">{message}</ol>
    </li>
  ) : null;

const generatePriceSummaryMessage = (
  type: string,
  price: Money,
  days: number,
  extraFlightPrice?: number,
  flightText?: string
): Node => {
  const { currency: curr, amount } = price;
  // this component is used to render porter as well since porter doesnt need flights.
  // we have to check whether the component type is guide or porter
  // to calculate the extra flight price.
  // Extra Flight Price is only added in Guides.
  const flightPriceText = extraFlightPrice ? (
    <li>Flights ({`${curr} ${extraFlightPrice} ${flightText}`})</li>
  ) : (
    ''
  );

  const dailyWages = (
    <li>
      Daily Wages, Accommodation and Meal's cost ({`${curr} ${amount} @ ${days} Days`})
    </li>
  );

  return (
    <Fragment>
      The cost includes :-{' '}
      <ol>
        {' '}
        {flightPriceText} {dailyWages}
      </ol>
    </Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdditionalServices);



// WEBPACK FOOTER //
// ./src/screens/Trek/Self-Guided/Review/AdditionalServices.js